import React from "react";

/**
 * The v2 table. Rows and columns on a desk; on a phone the same markup lays each row out as a
 * stacked card, one "label · value" line per cell, so a six-column table never needs sideways
 * scrolling. The phone layout is CSS on the `v2-table` class (tokens.css) keyed off each cell's
 * `label`, so a page writes its table once and gets both.
 *
 * `className` goes on the scroll wrapper — a wide table on a laptop can still scroll inside its
 * card, and on a phone the wrapper has nothing to scroll.
 */
export const Table: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = "", children }) => (
    <div className={`overflow-x-auto ${className}`}>
        <table className="v2-table w-full border-collapse">{children}</table>
    </div>
);

/** Header cell for the v2 tables — quiet, single-line, framed by the card's own borders. */
export const Th: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
    <th
        className={`whitespace-nowrap border-b border-t border-[var(--line)] px-[22px] py-3.5 text-left text-[13px] font-medium text-[var(--muted)] ${className}`}
    >
        {children}
    </th>
);

/**
 * `label` is the column heading again, for the phone layout: the header row is hidden there and
 * each cell names itself instead. Leave it off a cell that explains itself — an action button, a
 * status pill — and the phone shows the content alone, right-aligned.
 */
export const Td: React.FC<{ children?: React.ReactNode; className?: string; title?: string; label?: string }> = ({
    children,
    className = "",
    title,
    label,
}) => (
    <td
        title={title}
        data-label={label}
        className={`whitespace-nowrap border-b border-[var(--line)] px-[22px] py-[15px] text-[14px] ${className}`}
    >
        {children}
    </td>
);

/**
 * The bar above a table: title on the left, filters and actions pushed right.
 *
 * Right-justifying the controls is the design's rule — the title anchors the left edge and every
 * table on every page puts its filters in the same place, so the eye learns one spot.
 */
export const TableBar: React.FC<{ title: React.ReactNode; count?: React.ReactNode; children?: React.ReactNode }> = ({
    title,
    count,
    children,
}) => (
    <div className="mb-4 flex flex-wrap items-center gap-3.5 px-1">
        <span className="text-[22px] font-bold tracking-tight">{title}</span>
        {count !== undefined && <span className="text-[13px] font-medium text-[var(--muted)]">{count}</span>}
        <div className="ml-auto flex flex-wrap items-center gap-2.5">{children}</div>
    </div>
);
